import { useEffect, useMemo } from 'react';
import { differenceInMilliseconds } from 'date-fns';
import { noop } from 'lodash-es';
import { useStateInRef } from 'seagull';
import { useIoContext, useStore, useStoreActions, useStoreStatic } from '~/contexts';
import { ErrorResponse } from '~/infra/error-response/ErrorResponse';
import { makeCoalescingRunner } from './pendingPushRemovals.utils';
const SET_TIMEOUT_MAX_DELAY_MS = 2147483647;
const PUSH_REMOVAL_BI_EVENT = 'native_push_removal';
export const usePendingPushRemovals = ()=>{
    const { native, browser, getNow, logBIEvent } = useIoContext();
    const { api } = useStoreActions();
    const { getState } = useStoreStatic();
    const outbox = native.pushRemovalOutbox;
    const isEnabled = native.push.isAvailable;
    const isLoggedOut = useStore((state)=>state.authToken == null);
    const loadedUserId = useStore((state)=>{
        var _ref;
        var _state_userInfo;
        return (_ref = (_state_userInfo = state.userInfo) === null || _state_userInfo === void 0 ? void 0 : _state_userInfo.id()) !== null && _ref !== void 0 ? _ref : null;
    });
    const getDrainAndScheduleRetry = useStateInRef(drainAndScheduleRetry);
    const drainer = useMemo(()=>({
            runner: makeCoalescingRunner(()=>getDrainAndScheduleRetry()()),
            cancelScheduledDrain: noop
        }), []);
    useEffect(()=>{
        if (isEnabled) {
            drainer.runner.request();
        }
    }, [
        isEnabled,
        isLoggedOut,
        loadedUserId,
        drainer
    ]);
    useEffect(()=>{
        if (isEnabled) {
            const removeResumeListener = native.lifecycle.registerResumeListener(drainer.runner.request);
            const removeOnlineListener = browser.registerOnlineListener(drainer.runner.request);
            return ()=>{
                removeResumeListener();
                removeOnlineListener();
                drainer.cancelScheduledDrain();
            };
        }
        return undefined;
    }, [
        isEnabled,
        drainer
    ]);
    async function drainAndScheduleRetry() {
        var _ref;
        const { authToken, userInfo } = getState();
        const signedInUserId = (_ref = userInfo === null || userInfo === void 0 ? void 0 : userInfo.id()) !== null && _ref !== void 0 ? _ref : null;
        const isSignedInUserLoading = authToken != null && signedInUserId == null;
        if (!isSignedInUserLoading) {
            await drain(signedInUserId);
        }
        await scheduleNextRetry();
    }
    async function drain(signedInUserId) {
        const now = getNow();
        const expired = await outbox.clearExpired(now);
        expired.forEach((removal)=>report('expired', removal));
        const due = await outbox.due(now, signedInUserId);
        await Promise.allSettled(due.map(send));
    }
    async function send(removal) {
        const { token, platform, userId } = removal;
        const failure = await api.mobileNotification.unregisterDevice({
            token,
            platform,
            userId
        }).then(()=>null, (error)=>new ErrorResponse(error));
        if (failure === null || failure === void 0 ? void 0 : failure.isRetryableLater()) {
            const failedAt = getNow();
            await outbox.retryLater(removal, failedAt, failure.retryAfterMs(failedAt.getTime()));
            return;
        }
        await outbox.clear(removal);
        report(failure == null ? 'removed' : 'rejected', removal);
    }
    async function scheduleNextRetry() {
        const now = getNow();
        const retryAt = await outbox.nextRetryAt(now);
        drainer.cancelScheduledDrain();
        drainer.cancelScheduledDrain = retryAt == null ? noop : browser.schedule(Math.min(differenceInMilliseconds(retryAt, now), SET_TIMEOUT_MAX_DELAY_MS), drainer.runner.request);
    }
    function report(outcome, param) {
        let { attempts } = param;
        logBIEvent({
            type: PUSH_REMOVAL_BI_EVENT,
            page_name: PUSH_REMOVAL_BI_EVENT,
            hierarchy: [],
            outcome,
            attempts
        });
    }
};
